import type {components} from '../../shared/api/v1/openapi.types';
import {maxLength, required} from '../../utils/validation';

export type Connection = components['schemas']['Connection'];
export type FileGroups = components['schemas']['ConnectionFileGroupsDto'];
export type FormValues = components['schemas']['CreateConsumerConnectionDto'] & {
  pointsC: components['schemas']['ConnectionPointPowerDto'][];
  pointsB: components['schemas']['ConnectionPointPowerDto'][];
  personalAgreement: boolean;
};
export const FILE_KEYS = ['routes', 'ownership', 'misc', 'plan', 'energySchema', 'ukAgreement', 'contractInfoFile'] as const;
export type FileKey = typeof FILE_KEYS[number];
export const FILE_LABELS: Record<FileKey, string> = {
  routes: 'Паспорт / Доверенность или иные документы',
  ownership: 'Право собственности на присоединяемый объект',
  misc: 'Прочие документы', plan: 'План расположения', energySchema: 'Однолинейная схема',
  ukAgreement: 'Разрешение управляющей компании', contractInfoFile: 'Информация о реквизитах договора',
};
export const FIELD_LABELS: Record<string, string> = {
  type: 'Тип заявления', devNames: 'Наименование энергопринимающих устройств',
  region: 'Регион', address: 'Адрес', maxPower: 'Максимальная мощность',
  reason: 'Цель обращения', method: 'Способ передачи договора', reliability: 'Категория надёжности',
  personalAgreement: 'Согласие на передачу персональных данных',
};

const text = (value?: string) => value?.trim() || null;
const option = <T extends number>(value: number | undefined, options: readonly T[]): T | null =>
  options.find((item) => item === value) ?? null;

export const draftPayload = (values: FormValues, fileGroups: FileGroups): components['schemas']['SaveConnectionDraftDto'] => ({
  ...values,
  type: option(values.type, [1, 2, 3, 4] as const),
  loadNature: option(values.loadNature, [1, 2, 3] as const),
  reliability: option(values.reliability, [1, 2, 3] as const),
  reason: option(values.reason, [1, 2, 3, 4, 5, 6] as const),
  method: option(values.method, [0, 1, 2] as const),
  date: text(values.date), devNames: text(values.devNames), region: text(values.region),
  address: text(values.address), maxPower: text(values.maxPower), powerOwened: text(values.powerOwened),
  powerBefore: text(values.powerBefore), voltage: text(values.voltage), pointsNum: text(values.pointsNum),
  voltage1: text(values.voltage1), voltage2: text(values.voltage2), voltage3: text(values.voltage3),
  loadNatureFull: text(values.loadNatureFull), transNum: text(values.transNum), genNum: text(values.genNum),
  generator: text(values.generator), armor: text(values.armor), armorValue: text(values.armorValue),
  level1: text(values.level1), level2: text(values.level2), level3: text(values.level3),
  tempDates: text(values.tempDates), dates: text(values.dates), plans: text(values.plans), offers: text(values.offers),
  contractInfo: text(values.contractInfo), timeline: text(values.timeline), fileGroups,
});

export const validateValues = (values: FormValues, submit: boolean): Record<string, string> => {
  const errors: Record<string, string> = {};
  const lengths: Partial<Record<keyof FormValues, number>> = {
    devNames: 99, region: 99, address: 199, maxPower: 9, voltage1: 9,
    ...(values.type === 1 ? {tempDates: 99, contractInfo: 99} : {}),
    ...([2, 3, 4].includes(values.type) ? {powerOwened: 9, powerBefore: 9, voltage2: 9, voltage3: 9, loadNatureFull: 199, dates: 199, plans: 199} : {}),
    ...(values.type === 2 ? {transNum: 19, genNum: 19, generator: 199, armor: 199, armorValue: 199, level1: 9, level2: 9, level3: 9} : {}),
    ...(values.type === 3 ? {offers: 199} : {}),
  };
  Object.entries(lengths).forEach(([key, length]) => {
    const value = values[key as keyof FormValues];
    const error = typeof value === 'string' ? maxLength(value, length) : undefined;
    if (error) errors[key] = error;
  });
  if (values.type === 2) {
    (['pointsC', 'pointsB'] as const).forEach((key) => values[key].forEach((point, index) => {
      const error = maxLength(point.name, 99) ?? maxLength(point.value, 9);
      if (error) errors[`${key}.${index}`] = error;
    }));
  }
  if (submit) {
    if (![1, 2, 3, 4].includes(values.type)) errors.type = 'Выберите тип заявления';
    (['devNames', 'region', 'address', 'maxPower'] as const).forEach((key) => {
      const error = required(values[key]);
      if (error) errors[key] = error;
    });
    if (![1, 2, 3, 4, 5, 6].includes(values.reason ?? -1)) errors.reason = 'Выберите цель обращения';
    if (![0, 1, 2].includes(values.method ?? -1)) errors.method = 'Выберите способ передачи';
    if ([3, 4].includes(values.type) && ![1, 2, 3].includes(values.reliability ?? -1)) errors.reliability = 'Выберите категорию надёжности';
    if (!values.personalAgreement) errors.personalAgreement = 'Необходимо согласие';
  }
  return errors;
};
